import { Injectable } from '@angular/core';
import { Organization, Role, UserContext } from './user.model';
import { UsersApiService } from './users-api.service';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';

import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class SessionService {
    roleOverride$: BehaviorSubject<Role> = new BehaviorSubject<Role>('');
    originalRole$: Observable<Role> = this.auth.isAuthenticated$.pipe(
        filter((value) => value),
        switchMap(() => this.userApi.getCurrentUser$()),
        map((user) => user?.role ?? ''),
        shareReplay()
    );
    assumedRole$: Observable<Role> = this.roleOverride$.pipe(
        switchMap((role) => (role ? this.roleOverride$ : this.originalRole$))
    );
    isReadOnly$ = this.assumedRole$.pipe(map((role) => role === 'ORG_MEMBER'));
    readonly userContext$: Observable<UserContext | null> = this.originalRole$.pipe(
        filter(role => !!role),
        take(1),
        switchMap(role => role === 'ROOT_ADMIN'
            ? of(null)
            : this.userApi.getUserContext$().pipe(catchError(() => of(null)))
        ),
        shareReplay(1),
    );

    readonly isRolePickerNeeded$: Observable<boolean> = this.userContext$.pipe(
        switchMap(ctx => this.originalRole$.pipe(
            map(role => {
                if (role === 'ROOT_ADMIN') return true;
                if (!ctx) return false;
                return ctx.accounts.length > 0 && ctx.orgs.length > 0;
            })
        )),
        distinctUntilChanged(),
        shareReplay(1),
    );

    accountOrg$ = new BehaviorSubject<{
        account: { id: string; name: string } | null;
        organization: { id: string; name: string } | null;
    }>(this.storedAccountOrg);

    constructor(
        private auth: AuthService,
        private userApi: UsersApiService,
        private localStorageService: LocalStorageService
    ) { }

    get storedAccountOrg(): {
        account: { id: string; name: string } | null;
        organization: { id: string; name: string } | null;
        } {
        const accountId = this.localStorageService.get('accountId');
        const accountName = this.localStorageService.get('accountName');
        if (!accountId || !accountName) {
            console.log('no group stored');
            return { account: null, organization: null };
        }

        const orgId = this.localStorageService.get('organizationId');
        const orgName = this.localStorageService.get('organizationName');
        const org = orgId ? { id: orgId, name: orgName } : null;

        const assumedRole = this.localStorageService.get('assumedRole');
        if (assumedRole) {
            this.roleOverride$.next(assumedRole as Role);
        }

        return {
            account: { name: accountName, id: accountId },
            organization: org,
        };
    }

    setAccountOrg(group: { id: string; name: string }, organization: Organization | null = null) {
        this.localStorageService.set('accountId', group.id);
        this.localStorageService.set('accountName', group.name);

        if (organization) {
            this.localStorageService.set('organizationId', organization.org_id);
            this.localStorageService.set('organizationName', organization.name);
        } else {
            this.localStorageService.remove('organizationId');
            this.localStorageService.remove('organizationName');
        }

        this.accountOrg$.next({ account: group, organization: organization ? { id: organization.org_id, name: organization.name } : null });

        return combineLatest([this.originalRole$, this.assumedRole$]).pipe(
            take(1),
            tap(([originalRole, assumedRole]) => {
                if (originalRole !== assumedRole) {
                    this.localStorageService.set('assumedRole', assumedRole);
                } else {
                    this.localStorageService.remove('assumedRole');
                }
            })
        );
    }

    clearAccountOrg() {
        this.localStorageService.remove('accountId');
        this.localStorageService.remove('accountName');
        this.localStorageService.remove('organizationId');
        this.localStorageService.remove('organizationName');
        this.localStorageService.remove('assumedRole');
        this.accountOrg$.next({ account: null, organization: null });
    }
}
